import { Badge } from '@/components/ui/badge';
import { Lock, FileEdit, Archive } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Database } from '@/integrations/supabase/types';

type PolicyStatus = Database['public']['Enums']['policy_status'];

interface PolicyStatusBadgeProps {
  status: PolicyStatus;
}

// Map database status to display status
function getDisplayStatus(status: PolicyStatus): 'draft' | 'approved' | 'pending_approval' | 'expired' {
  if (status === 'Approved') {
    return 'approved';
  }
  if (status === 'draft') {
    return 'draft';
  }
  if (status === 'pending_approval') {
    return 'pending_approval';
  }
  return 'expired'; // expired status treated as archived/expired
}

type DisplayStatus = 'draft' | 'approved' | 'pending_approval' | 'expired';

const statusConfig: Record<DisplayStatus, { icon: React.ElementType; label: string; className: string }> = {
  draft: {
    icon: FileEdit,
    label: 'Draft',
    className: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-400',
  },
  approved: {
    icon: Lock,
    label: 'Approved',
    className: 'border-teal-300 bg-teal-50 text-teal-700 dark:border-teal-700 dark:bg-teal-950 dark:text-teal-400',
  },
  pending_approval: {
    icon: FileEdit,
    label: 'Pending Approval',
    className: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-400',
  },
  expired: {
    icon: Archive,
    label: 'Archived',
    className: 'border-gray-300 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-400',
  },
};

export function PolicyStatusBadge({ status }: PolicyStatusBadgeProps) {
  const displayStatus = getDisplayStatus(status);
  const config = statusConfig[displayStatus];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn('gap-1.5 font-medium', config.className)}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}
